/**
 * TodayActionQueue — "오늘의 할 일" 통합 액션 큐 (대시보드 재설계 Sprint 3 → 2026-10-14 워크스페이스 디자인)
 *
 * 모양은 레퍼런스 «Due this month» 목록:
 *   [머리] 세그먼트(전체·고객요청·마감·배정·정기) + 인박스 열기
 *   [AI 브리핑] 남색 고정폭 머리 + 한 문단 (있을 때만)
 *   [맨 위 한 건] 크게 펼친 카드 — 고리·제목·상태 꼬리표·마감 / 빗금 / 확인할 것 · «왜 지금» 상자 / 단추
 *   [나머지] 한 줄씩 — 고리 · 제목+출처 · 경과 · 상태 꼬리표 · 공유 · 마감 · 단추
 *   [바닥] 몇 건 중 몇 건 · 인박스 전체 보기
 *
 * 동작은 그대로다(완료·실행취소·오늘 숨기기·설치 완료 확인 창·NO_DONE_SOURCES). 표식(data-testid)도 그대로 둔다.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowUpRight, Check, EyeOff, Sparkles, Users, AlertTriangle, ChevronRight, PartyPopper,
} from 'lucide-react';
import { Button } from '../../../components/ui/shadcn/button';
import Tooltip from '../../../components/ui/Tooltip';
import EmptyState from '../../../components/ui/EmptyState';
import NotionSelect from '../../../components/ui/NotionSelect';
import { WsPill, WsRing, wsTone, type WsTone } from '../../../components/ws';
import { getStatusLabel } from '../../../constants/statusLabels';
import type {
  ActionQueueGroupKey, ActionQueueItem, ActionQueuePayload, QueueBriefing,
} from '../../../services/actionQueue';

const PRIORITY: Record<string, { label: string; tone: WsTone }> = {
  critical: { label: '긴급', tone: 'red' },
  high: { label: '높음', tone: 'orange' },
  normal: { label: '보통', tone: 'blue' },
  low: { label: '낮음', tone: 'gray' },
};

const SOURCE_BADGE: Record<string, string> = {
  customer_event: '고객요청',
  sla_breach: '마감',
  assigned: '배정',
  menu_task_cron: '정기',
  install_today: '설치',
  install_overdue: '설치 지연',
  meeting: '미팅',
  cash_receipt_request: '현금영수증',
  todo: '내 할 일',
};

// 완료 처리가 원본 설치일정 상태(→ '설치완료')까지 바꾸는 소스.
// 서버 action-queue.js 의 INSTALL_SOURCES 와 동일 집합이어야 한다.
const INSTALL_SOURCES = new Set(['install_today', 'install_overdue']);

// 큐에서 완료할 수 없는 소스 — 완료는 원래 화면에서 한다(행을 누르면 그 화면이 열린다).
// 서버 action-queue.js 의 NO_DONE_SOURCES 와 같은 집합이어야 한다(서버는 400 으로 막는다).
const NO_DONE_SOURCES = new Set(['todo']);

const EVENT_KEY_LABEL: Record<string, string> = {
  'home_styling.create': '홈스타일링 신청',
  'repair_request.create': '평생수선 신청',
  'install.request.create': '설치 신청',
  'install.schedule.change_request': '설치 변경요청',
  'install.request.cancel': '설치 취소',
};

type FilterKey = 'all' | ActionQueueGroupKey;

/** 그룹 안 정렬 — 기본은 서버 순서(우선순위). 레퍼런스의 «By deadline» 자리 */
type SortKey = 'priority' | 'due' | 'oldest';
const SORTS: Array<{ value: SortKey; label: string }> = [
  { value: 'priority', label: '우선순위순' },
  { value: 'due', label: '마감 빠른 순' },
  { value: 'oldest', label: '오래된 순' },
];
const dueMs = (it: ActionQueueItem) => (it.due_at ? new Date(it.due_at).getTime() : Number.POSITIVE_INFINITY);
const ageOf = (it: ActionQueueItem) => it.age_hours ?? -1;
function sortItems(items: ActionQueueItem[], key: SortKey): ActionQueueItem[] {
  if (key === 'priority') return items;
  const copy = [...items];
  return key === 'due' ? copy.sort((a, b) => dueMs(a) - dueMs(b)) : copy.sort((a, b) => ageOf(b) - ageOf(a));
}

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: '전체' },
  { key: 'customer_request', label: '고객요청' },
  { key: 'sla', label: '마감' },
  { key: 'assigned', label: '배정' },
  { key: 'routine', label: '정기' },
];

const HOUR = 3_600_000;

/** 상태값 → 한글. ⛔ 서버가 «상태: instruction_pending» 처럼 영문 코드를 문장에 넣어 보낸다 — 화면에서는 한글로(한글 우선 규칙) */
function statusText(item: ActionQueueItem): string | null {
  if (!item.status) return null;
  const label = item.menu ? getStatusLabel(item.menu, item.status) : item.status;
  return /[가-힣]/.test(label) ? label : null;
}
function whyText(item: ActionQueueItem): string | null {
  if (!item.why || !item.status) return item.why;
  const label = statusText(item);
  return label ? item.why.split(item.status).join(label) : item.why;
}

function formatAge(item: ActionQueueItem): string | null {
  if (item.age_hours == null) return null;
  if (item.age_hours < 1) return '방금 전';
  if (item.age_hours < 24) return `${item.age_hours}시간 경과`;
  return `${Math.floor(item.age_hours / 24)}일 경과`;
}

/** 마감까지 — 오른쪽 두 줄(굵은 날짜 / 흐린 상대 시간) */
function dueText(item: ActionQueueItem): { main: string; sub: string; danger: boolean } {
  if (!item.due_at) {
    return { main: '마감 없음', sub: formatAge(item) || '', danger: false };
  }
  const due = new Date(item.due_at);
  const diff = due.getTime() - Date.now();
  const main = due.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric', weekday: 'short' });
  if (diff < 0) {
    const d = Math.floor(-diff / (24 * HOUR));
    return { main, sub: d >= 1 ? `${d}일 지남` : `${Math.max(1, Math.floor(-diff / HOUR))}시간 지남`, danger: true };
  }
  const h = Math.floor(diff / HOUR);
  return { main, sub: h < 24 ? `${Math.max(1, h)}시간 남음` : `${Math.ceil(diff / (24 * HOUR))}일 남음`, danger: false };
}

/** 고리 — 마감이 있으면 «받은 때 → 마감» 중 얼마나 왔나, 없으면 점선 빈 고리 */
function ringOf(item: ActionQueueItem): { value: number | null; accent: number; tone: WsTone; label: string } {
  if (!item.due_at) return { value: null, accent: 0, tone: 'amber', label: '–' };
  const due = new Date(item.due_at).getTime();
  const start = item.created_at ? new Date(item.created_at).getTime() : due - 72 * HOUR;
  const now = Date.now();
  if (now >= due) return { value: 0, accent: 100, tone: 'red', label: `D+${Math.max(1, Math.ceil((now - due) / (24 * HOUR)))}` };
  const frac = Math.max(0, Math.min(1, (now - start) / Math.max(HOUR, due - start)));
  const daysLeft = Math.ceil((due - now) / (24 * HOUR));
  // 마지막 15% 는 호박색 꼬리 — «곧»
  return {
    value: Math.max(0, frac * 100 - 15),
    accent: Math.min(15, frac * 100),
    tone: item.imminent ? 'red' : 'amber',
    label: daysLeft <= 0 ? 'D-0' : `D-${daysLeft}`,
  };
}

/** 상태 꼬리표 — 마감 지남 > 임박 > 우선순위 */
function stateOf(item: ActionQueueItem): { label: string; tone: WsTone } {
  if (item.overdue) return { label: '마감 지남', tone: 'red' };
  if (item.imminent) return { label: '임박', tone: 'amber' };
  return PRIORITY[item.priority] || PRIORITY.normal;
}

interface Props {
  queue: ActionQueuePayload | null;
  loading: boolean;
  error: string;
  briefing: QueueBriefing | null;
  briefingLoading: boolean;
  onRetry: () => void;
  /**
   * 완료 처리 — 결과에 따라 컴포넌트가 스낵바를 분기한다:
   *  'done' → "실행취소" 스낵바 / 'idempotent' → 실행취소 없는 안내 / 'fail' → 무동작(훅이 에러 토스트)
   */
  onDone: (item: ActionQueueItem) => Promise<'done' | 'idempotent' | 'fail'>;
  /** 실행취소 스낵바의 "실행취소" 클릭 시 호출 */
  onUndo: (item: ActionQueueItem) => void | Promise<void>;
  onSnooze: (item: ActionQueueItem) => void;
}

const RowSkeleton: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 4px', borderTop: '1px solid var(--ws-line-soft)' }}>
    <div className="ws-skel" style={{ width: 46, height: 46, borderRadius: '50%' }} />
    <div style={{ flex: 1 }}>
      <div className="ws-skel" style={{ height: 14, width: '42%' }} />
      <div className="ws-skel" style={{ height: 12, width: '28%', marginTop: 8 }} />
    </div>
  </div>
);

export const TodayActionQueue: React.FC<Props> = ({
  queue, loading, error, briefing, briefingLoading, onRetry, onDone, onUndo, onSnooze,
}) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sort, setSort] = useState<SortKey>('priority');

  /** 완료 성공 시 실행취소 스낵바 표시 — 6초, "실행취소" 클릭 시 onUndo 호출 */
  const handleDone = useCallback(
    async (item: ActionQueueItem) => {
      // 설치 항목의 완료는 설치일정 상태까지 '설치완료' 로 바꾸는 되돌리기 어려운 처리다.
      // 실행 전 확인을 받고, 오늘만 숨기려는 사용자는 옆의 숨기기 버튼으로 유도한다.
      if (INSTALL_SOURCES.has(item.source)) {
        const ok = window.confirm(
          `'${item.title}' 을 완료 처리합니다.\n\n`
          + `설치일정 상태도 '설치완료' 로 함께 변경되어 할 일 목록에서 영구히 사라집니다.\n`
          + `되돌리려면 설치일정 관리 화면에서 상태를 다시 바꿔야 합니다.\n\n`
          + `오늘 하루만 숨기려면 [취소] 후 눈 모양 숨기기 버튼을 사용하세요.`,
        );
        if (!ok) return;
      }

      const result = await onDone(item);
      if (result === 'fail') return;
      if (result === 'idempotent') {
        // 이미 다른 담당자가 완료 — 실행취소 어포던스 없이 안내만 (undo 무효, 큐는 훅이 재조회)
        toast('이미 다른 담당자가 완료한 항목입니다', { icon: 'ℹ️' });
        return;
      }
      // 설치 항목은 실행취소를 제공하지 않는다. undo 는 완료 기록만 지우고
      // 설치일정 상태(completed)는 되돌리지 않아, 실행취소를 눌러도 항목이 돌아오지 않는다.
      // 잘못 눌렀다면 설치일정 관리 화면에서 상태를 바꾸는 것이 정확한 경로다.
      if (INSTALL_SOURCES.has(item.source)) {
        toast.success("완료 처리했습니다 — 설치일정 상태도 '설치완료' 로 변경되었습니다", { duration: 6000 });
        return;
      }
      toast(
        (t) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            완료 처리했습니다
            <Button
              variant="ghost"
              size="sm"
              data-testid="aq-undo-btn"
              className="ws-btn ws-btn--ghost ws-btn--sm"
              style={{ color: 'var(--ws-blue-ink)' }}
              onClick={() => {
                toast.dismiss(t.id);
                onUndo(item);
              }}
            >
              실행취소
            </Button>
          </span>
        ),
        { duration: 6000 },
      );
    },
    [onDone, onUndo],
  );

  const commentByHash = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of briefing?.top_actions || []) map.set(a.hash, a.comment);
    return map;
  }, [briefing]);

  const visibleGroups = useMemo(() => {
    const groups = (queue?.groups || []).map((g) => ({ ...g, items: sortItems(g.items, sort) }));
    return filter === 'all' ? groups : groups.filter((g) => g.key === filter);
  }, [queue, filter, sort]);

  const total = queue?.total ?? 0;
  const shown = visibleGroups.reduce((s, g) => s + g.count, 0);
  const featured = visibleGroups.find((g) => g.count > 0)?.items[0];
  const featuredHash = featured?.hash;

  /** 단추 셋 — 바로가기 / 완료 / 오늘 숨기기 (표식 그대로) */
  const actions = (item: ActionQueueItem, big = false) => {
    const doneLabel = INSTALL_SOURCES.has(item.source)
      ? "완료 처리 — 설치일정 상태도 '설치완료' 로 바뀌고 목록에서 영구히 사라집니다"
      : item.shared
        ? '완료 처리 — 모든 담당자 목록에서 함께 제거됩니다'
        : '완료 처리 (오늘 목록에서 제거)';
    if (big) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
          {item.reference_url && (
            <Button type="button" variant="ghost" className="ws-btn ws-btn--primary" onClick={() => navigate(item.reference_url!)}>
              해당 화면 열기 <ArrowUpRight size={15} aria-hidden />
            </Button>
          )}
          {!NO_DONE_SOURCES.has(item.source) && (
            <Tooltip label={doneLabel}>
              <Button
                type="button" variant="ghost" aria-label="완료 처리" data-testid={`aq-done-${item.hash}`}
                className="ws-btn ws-btn--amber" onClick={() => handleDone(item)}
              >
                <Check size={15} aria-hidden /> 완료 처리
              </Button>
            </Tooltip>
          )}
          <Tooltip label="오늘 하루 숨기기 (내일 다시 표시)">
            <Button
              type="button" variant="ghost" aria-label="오늘 하루 숨기기" data-testid={`aq-snooze-${item.hash}`}
              className="ws-btn ws-btn--ghost" onClick={() => onSnooze(item)}
            >
              <EyeOff size={15} aria-hidden /> 오늘 숨기기
            </Button>
          </Tooltip>
        </div>
      );
    }
    return (
      <div className="aq-row-actions" onClick={(e) => e.stopPropagation()}>
        {item.reference_url && (
          <Tooltip label="해당 화면으로 이동">
            <Button
              type="button" variant="ghost" aria-label="해당 화면으로 이동"
              className="ws-btn ws-btn--ghost ws-btn--icon ws-btn--sm" onClick={() => navigate(item.reference_url!)}
            >
              <ArrowUpRight size={15} />
            </Button>
          </Tooltip>
        )}
        {!NO_DONE_SOURCES.has(item.source) && (
          <Tooltip label={doneLabel}>
            <Button
              type="button" variant="ghost" aria-label="완료 처리" data-testid={`aq-done-${item.hash}`}
              className="ws-btn ws-btn--ghost ws-btn--icon ws-btn--sm" style={{ color: 'var(--ws-green-ink)' }}
              onClick={() => handleDone(item)}
            >
              <Check size={15} />
            </Button>
          </Tooltip>
        )}
        <Tooltip label="오늘 하루 숨기기 (내일 다시 표시)">
          <Button
            type="button" variant="ghost" aria-label="오늘 하루 숨기기" data-testid={`aq-snooze-${item.hash}`}
            className="ws-btn ws-btn--ghost ws-btn--icon ws-btn--sm" style={{ color: 'var(--ws-ink-4)' }}
            onClick={() => onSnooze(item)}
          >
            <EyeOff size={15} />
          </Button>
        </Tooltip>
      </div>
    );
  };

  const sourceLabel = (item: ActionQueueItem) => (item.event_key ? EVENT_KEY_LABEL[item.event_key] : null) || SOURCE_BADGE[item.source] || item.source;

  // ⛔ «공유» 글자를 남긴다 — 아이콘만 두면 무슨 뜻인지 알 수 없고 E2E(공유 배지 문구)도 깨진다
  const sharedMark = (item: ActionQueueItem) => item.shared && (
    <span data-testid={`aq-shared-${item.hash}`} title="팀 공유 — 한 명이 완료하면 모두의 목록에서 빠집니다">
      <WsPill tone="purple" size="sm" icon={<Users size={11} aria-hidden />}>공유</WsPill>
    </span>
  );

  /** 맨 위 한 건 — 크게 */
  const renderFeatured = (item: ActionQueueItem) => {
    const ring = ringOf(item);
    const st = stateOf(item);
    const due = dueText(item);
    const why = commentByHash.get(item.hash) || whyText(item);
    const checks = [
      item.customer_name && { t: item.customer_name, s: '고객' },
      // ⛔ 상태값은 영문 코드일 수 있다(instruction_pending 등) — 한글 라벨이 있을 때만 보여 준다(한글 우선 규칙)
      statusText(item) && { t: statusText(item)!, s: '현재 상태' },
      formatAge(item) && { t: formatAge(item)!, s: item.created_at ? new Date(item.created_at).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' 받음' : '받은 뒤' },
    ].filter(Boolean) as Array<{ t: string; s: string }>;
    return (
      <div
        key={item.hash}
        data-testid={`action-queue-item-${item.hash}`}
        className="ws-card aq-featured"
        onClick={() => item.reference_url && navigate(item.reference_url)}
        style={{ cursor: item.reference_url ? 'pointer' : 'default' }}
      >
        <div className="aq-featured-head">
          <WsRing value={ring.value} accent={ring.accent} accentTone={ring.tone} label={ring.label} size={52} title="받은 때부터 마감까지 지난 정도" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
              <span className="ws-truncate" style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{item.title}</span>
              <span className="ws-mono ws-faint" style={{ fontSize: 12.5, flexShrink: 0 }}>{sourceLabel(item)}</span>
            </div>
            <div className="ws-sub ws-truncate" style={{ marginTop: 4, fontSize: 14.5 }}>{whyText(item) || '세부 사항은 해당 화면에서 확인하세요'}</div>
          </div>
          <WsPill tone={st.tone}>{st.label}</WsPill>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div className="ws-num" style={{ fontSize: 16, fontWeight: 650 }}>{due.main}</div>
            <div className="ws-num" style={{ fontSize: 13.5, marginTop: 2, color: due.danger ? 'var(--ws-red-ink)' : 'var(--ws-ink-3)' }}>{due.sub}</div>
          </div>
        </div>

        <div className="ws-hatch" style={{ margin: '18px 0 20px' }} />

        <div className="aq-featured-body">
          <div style={{ minWidth: 0 }}>
            <div className="ws-eyebrow">확인할 것 · {checks.length}</div>
            <ul className="aq-checks">
              {checks.length === 0 && <li><span>세부 정보 없음</span><small>해당 화면에서 확인</small></li>}
              {checks.map((c) => <li key={c.s}><span>{c.t}</span><small>{c.s}</small></li>)}
            </ul>
          </div>
          <div className={`ws-callout ${wsTone(item.overdue || item.priority === 'critical' ? 'red' : 'amber')}`} data-testid={commentByHash.get(item.hash) ? 'aq-ai-comment' : undefined}>
            <div className="ws-callout-title">
              {commentByHash.get(item.hash) ? <Sparkles size={16} aria-hidden /> : <AlertTriangle size={16} aria-hidden />}
              왜 지금 봐야 하나요
            </div>
            <div className="ws-callout-body">{why || '마감·우선순위가 가장 앞선 일입니다. 먼저 처리하면 나머지 목록이 줄어듭니다.'}</div>
            <div style={{ borderTop: '1px solid var(--tone-line)', marginTop: 14, paddingTop: 10, display: 'flex', gap: 10, fontSize: 13.5 }}>
              <span style={{ opacity: 0.8, whiteSpace: 'nowrap' }}>출처</span>
              {/* ⛔ item.menu 는 영문 코드(installation_schedule 등)라 보여 주지 않는다 */}
              <span style={{ fontWeight: 600 }}>{sourceLabel(item)}</span>
            </div>
          </div>
        </div>

        <div className="aq-featured-foot">
          {actions(item, true)}
          {sharedMark(item)}
        </div>
      </div>
    );
  };

  const renderRow = (item: ActionQueueItem) => {
    const ring = ringOf(item);
    const st = stateOf(item);
    const due = dueText(item);
    const aiComment = commentByHash.get(item.hash);
    return (
      <div
        key={item.hash}
        data-testid={`action-queue-item-${item.hash}`}
        className="aq-row"
        role={item.reference_url ? 'link' : undefined}
        tabIndex={item.reference_url ? 0 : undefined}
        onClick={() => item.reference_url && navigate(item.reference_url)}
        onKeyDown={(e) => { if (e.key === 'Enter' && item.reference_url) navigate(item.reference_url); }}
        style={{ cursor: item.reference_url ? 'pointer' : 'default' }}
      >
        <WsRing value={ring.value} accent={ring.accent} accentTone={ring.tone} label={ring.label} title="받은 때부터 마감까지 지난 정도" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span className="ws-truncate" style={{ fontSize: 15, fontWeight: 550 }}>{item.title}</span>
            {sharedMark(item)}
          </div>
          <div className="ws-truncate" style={{ marginTop: 3, fontSize: 13.5, color: aiComment ? 'var(--ws-blue-ink)' : 'var(--ws-ink-3)' }}>
            <span className="ws-mono" style={{ fontSize: 12, color: 'var(--ws-ink-4)', marginRight: 8 }}>{sourceLabel(item)}</span>
            {aiComment ? (
              <><Sparkles size={12} aria-hidden style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />{aiComment}</>
            ) : (whyText(item) || item.customer_name || '')}
          </div>
        </div>
        <span className="aq-row-pill"><WsPill tone={st.tone} size="sm">{st.label}</WsPill></span>
        <div className="aq-row-end">
          <div className="aq-row-due">
            <div className="ws-num" style={{ fontSize: 14.5, fontWeight: 650 }}>{due.main}</div>
            <div className="ws-num" style={{ fontSize: 13, marginTop: 2, color: due.danger ? 'var(--ws-red-ink)' : 'var(--ws-ink-3)' }}>{due.sub}</div>
          </div>
          {actions(item)}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="today-action-queue" id="today-action-queue" className="ws-bezel">
      <div className="ws-card" style={{ padding: '22px 26px 8px' }}>
        {/* 머리 — 세그먼트 + 인박스 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="ws-seg" data-testid="action-queue-filters" role="group" aria-label="오늘의 할 일 유형">
            {FILTERS.map((f) => {
              const active = filter === f.key;
              const count = f.key === 'all' ? total : queue?.groups.find((g) => g.key === f.key)?.count ?? 0;
              return (
                <Button
                  key={f.key}
                  type="button"
                  variant="ghost"
                  className="ws-seg-item"
                  aria-pressed={active}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                  <span className="ws-seg-count">{count}</span>
                </Button>
              );
            })}
          </div>
          {/* 인박스로 가는 길은 목록 바닥(«인박스 전체 보기») 하나로 — 머리에 두면 좁은 화면에서 세그먼트와 줄이 갈린다 */}
          <div style={{ width: 150 }} data-testid="action-queue-sort">
            <NotionSelect value={sort} onChange={(v) => setSort(v as SortKey)} options={SORTS} size="medium" />
          </div>
        </div>

        <h2 className="ws-h2" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>오늘의 할 일</h2>

        {/* AI 브리핑 — 없으면 그리지 않는다(우아한 강등) */}
        {briefingLoading && total > 0 && (
          <div className="ws-eyebrow" style={{ marginTop: 18, color: 'var(--ws-blue-ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={13} className="animate-pulse" aria-hidden /> AI 브리핑 · 만드는 중
          </div>
        )}
        {!briefingLoading && briefing?.briefing && (
          <div data-testid="ai-briefing" style={{ marginTop: 18 }}>
            <div className="ws-eyebrow" style={{ color: 'var(--ws-blue-ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={13} aria-hidden /> AI 브리핑 · 오늘 아침
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.6, color: 'var(--ws-ink-2)' }}>{briefing.briefing}</p>
          </div>
        )}

        {/* 본문 */}
        <div style={{ marginTop: 18 }}>
          {loading && !queue ? (
            <div><div className="ws-skel" style={{ height: 180, borderRadius: 'var(--ws-r-xl)', marginBottom: 8 }} /><RowSkeleton /><RowSkeleton /><RowSkeleton /></div>
          ) : error && !queue ? (
            <EmptyState variant="error" title="오늘의 할 일을 불러오지 못했습니다" description={error} onRetry={onRetry} />
          ) : total === 0 ? (
            <EmptyState
              icon={PartyPopper}
              title="오늘 처리할 일이 없습니다"
              description="새 고객 요청·마감 임박 업무가 생기면 여기에 표시됩니다"
            />
          ) : (
            <>
              {/* 맨 위 한 건 — 크게 */}
              {featured && renderFeatured(featured)}
              {/* 나머지 — 목록이 길면 이 안에서만 스크롤한다(페이지가 끝없이 길어지지 않게). 항목은 모두 그려 둔다 */}
              <div className="aq-scroll">
              {visibleGroups.filter((g) => g.count > 0).map((group) => (
                <section key={group.key} id={`aq-group-${group.key}`} data-testid={`action-queue-group-${group.key}`}>
                  <div className="aq-group-head">
                    <span className="ws-eyebrow">{group.label}</span>
                    <span className="ws-mono ws-faint" style={{ fontSize: 12 }}>{group.count}</span>
                  </div>
                  {/* 행 목록 — SLA 그룹 래퍼에 레거시 testid 유지 */}
                  <div data-testid={group.key === 'sla' ? 'ai-task-suggestions' : undefined}>
                    {group.items.filter((it) => it.hash !== featuredHash).map(renderRow)}
                  </div>
                </section>
              ))}
              </div>
              {visibleGroups.every((g) => g.count === 0) && (
                <EmptyState title="해당 유형의 할 일이 없습니다" description="다른 유형을 선택해 보세요" />
              )}
            </>
          )}
        </div>

        {/* 바닥 */}
        {total > 0 && (
          <div className="aq-foot">
            <span>{FILTERS.find((f) => f.key === filter)?.label} {shown.toLocaleString()}건 · 전체 할 일 {total.toLocaleString()}건</span>
            <Button type="button" variant="ghost" className="ws-btn ws-btn--link" onClick={() => navigate('/notifications?tab=unresolved')}>
              인박스 전체 보기 <ChevronRight size={15} aria-hidden />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodayActionQueue;
